/**
 * User Manual content structure - derived from User Manual - MahaAgX v1.0.docx
 * Figures use semantic PNG filenames under /documents/user-manual/images/ (exported from the manual).
 */
import { getFigureImage } from './user-manual-figures';
import { USER_MANUAL_SECTION_4 } from './user-manual-section-4-content';
import { USER_MANUAL_SECTION_5 } from './user-manual-section-5-content';
import type { ManualRoleAudienceRow, ManualSection } from './user-manual-types';

export type { ManualContentBlock, ManualRoleAudienceRow, ManualSection } from './user-manual-types';

export const MANUAL_INTENDED_AUDIENCE_TABLE: ManualRoleAudienceRow[] = [
  {
    roleName: 'Consumers',
    roleQualifier: 'With/Without Compute',
    coreHtml:
      'Browse, query, and access assets including <strong>Datasets</strong>, <strong>Models</strong>, and <strong>Use Cases</strong> to drive analysis or engineer novel AI solutions.',
    idealFor:
      'Govt. Data Officers, AI Startups, Policy Makers, Researchers, Analysts, and Coding Enthusiasts.',
  },
  {
    roleName: 'Providers',
    roleQualifier: 'With/Without Compute',
    coreHtml:
      'Primary contributors responsible for the <strong>ingestion</strong>, <strong>annotation</strong>, and <strong>lifecycle management</strong> of high-quality datasets.',
    idealFor: 'AI Researchers, NGOs, Private Sector Firms, and Govt. Data Custodians.',
  },
  {
    roleName: 'Organization Managers',
    coreHtml:
      'Strategic leads focused on <strong>Data Governance</strong>, defining granular access protocols, and auditing platform utility via usage reports.',
    idealFor: 'Department Heads, Startup Founders, and Project Coordinators.',
  },
];

export { getFigureImage } from './user-manual-figures';

export const USER_MANUAL_SECTIONS: ManualSection[] = [
  {
    id: 'overview',
    title: '1. Overview and Purpose',
    level: 1,
    /* Doc: level 1 is the chapter title only; all opening narrative sits under 1.1 (single combined block). */
    content: [],
    figures: [],
  },
  {
    id: 'intro-mahaagx',
    title: '1.1. Introduction to MahaAgX',
    level: 2,
    content: [
      'MahaAgX is a strategic initiative by the Department of Agriculture, Govt. of Maharashtra, designed to catalyze secure data exchange and agricultural innovation. By providing a robust ecosystem of rich agricultural Datasets, open-source Models, and a high-performance Innovation Sandbox featuring GPU-based compute and integrated development tools, MahaAgX empowers the creation of scalable AI Use Cases. Our mission is to transform raw data into real-world solutions that address the critical challenges of our farmers. This platform stands as a testament to Maharashtra\'s commitment to digital leadership, climate-resilient farming, and technology-driven agricultural governance.',
      'MahaAgX, a flagship initiative of the Department of Agriculture, Government of Maharashtra, is dedicated to accelerating agricultural innovation and ensuring secure data exchange. The platform provides a powerful ecosystem for creating scalable AI use cases by offering a wealth of agricultural datasets, open-source Models, and a high-performance Innovation Sandbox. This Sandbox is equipped with GPU-based compute and integrated development tools. Our core mission is to convert raw data into practical, real-world solutions that tackle the pressing issues faced by our farmers. MahaAgX embodies Maharashtra\'s commitment to digital leadership, technology-driven agricultural governance, and building a climate-resilient farming future.',
    ],
    figures: [],
  },
  {
    id: 'about-manual',
    title: '1.2. About this User Manual',
    level: 2,
    content: [
      'This section explains the objective, and best ways to use this guide.',
      '1. Purpose:',
      'The MahaAgX User Manual is the comprehensive guide for the platform. It is structured to enhance technical workflows and optimize platform efficiency, covering the entire process from initial data discovery to sophisticated model deployment.',
      '2. Intended audience:',
      'This guide is tailored to the diverse stakeholders within the MahaAgX ecosystem, ensuring every user—regardless of their technical infrastructure—can effectively leverage the platform\'s assets.',
    ],
    roleAudienceTable: MANUAL_INTENDED_AUDIENCE_TABLE,
    figures: [],
  },
  {
    id: 'navigating-home',
    title: '2. Navigating the Home Page',
    level: 1,
    content: [
      'The MahaAgX Home Page is the central entry point and primary launchpad for the platform, offering a unified dashboard for discovering assets and accessing platform functionalities.',
    ],
    figures: [],
  },
  {
    id: 'core-access',
    title: '2.1. Core Platform Access Points',
    level: 2,
    content: [
      'The single-entry interface provides immediate access to the platform\'s key features:',
      '● Asset Discovery: Find curated Datasets and high-performance Pre-trained Models.',
      '● Solutions & Development: Explore real-world Use Cases or engage in rapid experimentation within the Sandbox IDE.',
      '● Community & Support: Navigate to community modules for collaboration, discussion on datasets, and participation in challenges.',
    ],
    figures: [],
  },
  {
    id: 'global-nav',
    title: '2.2. The Global Navigation Bar',
    level: 2,
    content: [
      'The MahaAgX Navigation Bar serves as a persistent global header, ensuring a seamless one-click transition between core modules and essential account functions:',
      '● MahaAgX Logo: The global "Home" anchor, instantly returning the user to the main dashboard.',
      '● About Us: Offers a deep dive into the MahaAgX vision, core values, and ecosystem objectives.',
      '● Datasets: A direct portal for browsing, searching, and applying advanced filters to data repositories.',
      '● Models: Grants access to the comprehensive gallery of pre-trained Models optimized for immediate download and deployment.',
      '● Use Cases: A curated showcase of real-world implementations to inspire AI innovation and architectural design.',
      '● Sandbox IDE: Launches the integrated, in-browser development environment for seamless coding, testing, and model training.',
      '● Leaderboard: Meet the visionary leaders driving innovation in agricultural technology and data science at MahaAgX.',
      '● Challenges: Participate in exciting agricultural challenges and competitions to showcase your data science and machine learning skills.',
      '● Discussions: Join conversations, share insights, and collaborate with the community.',
      '● Resources: Access \'Toolsets\', \'Learning Library\', \'Knowledge Hub\' and \'Contact Us\'.',
      '● Accessibility Options: Customizable viewing features, including high-contrast mode, text scaling, and screen-reader optimizations for an inclusive user experience.',
      '● Sign in / Register: The gateway for user authentication or establishing a new community profile.',
      '● User Icon (Post-Authentication): Provides a direct link to the Personal Dashboard and centralized activity hub.',
      'The navigation bar is engineered for full responsiveness, optimizing dynamically for desktop and mobile environments. On smaller screens, it intelligently transitions into a compact hamburger menu to keep primary tools accessible without compromising screen real estate.',
    ],
    figures: [
      { caption: 'Figure 1 — Pre-login Navigation Bar', image: getFigureImage(1) },
      { caption: 'Figure 2 — Post-login Navigation Bar', image: getFigureImage(2) },
    ],
  },
  {
    id: 'hero-search',
    title: '2.3. Hero section and Global search',
    level: 2,
    content: [
      'The MahaAgX platform immediately engages users with a bold Hero section, featuring the taglines. This clean, attention-grabbing design highlights the platform\'s central promise. For resource discovery, a centrally located, all-in-one global search bar allows users to find Datasets, Models, and Use cases by entering keywords. The intelligent search provides real-time suggestions, grouped by category, and clicking "Go" delivers a consolidated results page. This combination of an engaging introduction and a smart search tool ensures users can quickly and efficiently access the resources they require.',
    ],
    figures: [{ caption: 'Figure 3 — Hero section and Global search bar', image: getFigureImage(3) }],
  },
  {
    id: 'initiative-by',
    title: '2.4. Initiative By',
    level: 2,
    content: [
      'This section emphasizes MahaAgX\'s role as a unifying hub, where different government departments join forces to advance AI-driven innovation for Maharashtra.',
    ],
    figures: [{ caption: 'Figure 4 — Initiative By section with logos', image: getFigureImage(4) }],
  },
  {
    id: 'core-features',
    title: '2.5. Core MahaAgX Features',
    level: 2,
    content: [
      'MahaAgX\'s data-driven innovation is built upon three core pillars: Datasets, Models, and Use Cases. To delve deeper into any module, simply click on the corresponding card.',
    ],
    figures: [{ caption: 'Figure 5 — MahaAgX core features section', image: getFigureImage(5) }],
  },
  {
    id: 'home-about',
    title: '2.6. About',
    level: 2,
    content: [
      'This brief "About" section spotlights MahaAgX\'s mission.',
    ],
    figures: [{ caption: 'Figure 6 — About section', image: getFigureImage(6) }],
  },
  {
    id: 'home-faqs',
    title: '2.7. Frequently Asked Questions (FAQs)',
    level: 2,
    content: [
      'This FAQ section provides clear, categorized answers to common MahaAgX inquiries.',
    ],
    figures: [{ caption: 'Figure 8 — FAQs section', image: getFigureImage(8) }],
  },
  {
    id: 'home-footer-cta',
    title: '2.8. Call to Action and Footer',
    level: 2,
    content: [
      'The MahaAgX Home Page prominently features a "Register Now" or "Contact Us" banner at the bottom. This encourages new users to join the community, explore its offerings—including Datasets, Models, and Use cases—and start developing AI solutions.',
      'The platform\'s footer serves as a vital navigation hub, reinforcing the MahaAgX identity through logo and mission statement and offering quick access to important links and API documentation for developers.',
    ],
    figures: [{ caption: 'Figure 9 — Call to Action and Footer section', image: getFigureImage(9) }],
  },
  {
    id: 'user-login-registration-join-orgs',
    title: '3. Login/Registration for users and joining organizations',
    level: 1,
    content: [
      'This section explains how users register and sign in to MahaAgX, recover a forgotten password, join an existing organisation, or register a new organisation to unlock provider and organisation manager workflows.',
    ],
    figures: [],
  },
  {
    id: 'registration',
    title: '3.1. Registration process for users',
    level: 2,
    content: [],
    contentBlocks: [
      { type: 'text', text: 'Step 1: Click Register on the Home Page.' },
      { type: 'figure', caption: 'Figure 10 — Click Register button', image: getFigureImage(10) },
      { type: 'text', text: 'Step 2: Enter first name, last name and email address.' },
      { type: 'figure', caption: 'Figure 11 — Enter first name, last name and email address', image: getFigureImage(11) },
      { type: 'text', text: 'Step 3: Create a strong password that meets the on-screen requirements.' },
      { type: 'figure', caption: 'Figure 12 — Creation of strong password', image: getFigureImage(12) },
      { type: 'text', text: 'Step 5: Verify your email by clicking the link sent to your inbox.' },
      { type: 'figure', caption: 'Figure 14 — Verification link sent', image: getFigureImage(14) },
      { type: 'figure', caption: 'Figure 15 — Verification email received in user\'s inbox', image: getFigureImage(15) },
      { type: 'text', text: 'Step 6: Access your dashboard—after verification you\'ll be signed in automatically with the default "Consumer" role.' },
      { type: 'figure', caption: 'Figure 16 — Post-login consumer dashboard', image: getFigureImage(16) },
    ],
    figures: [],
  },
  {
    id: 'login',
    title: '3.2. Login process for registered users',
    level: 2,
    content: [],
    contentBlocks: [
      { type: 'text', text: 'Step 1: Click Sign In on the Home Page.' },
      { type: 'figure', caption: 'Figure 17 — Clicking on Sign In button', image: getFigureImage(17) },
      { type: 'text', text: 'Step 2: Enter your email address and password.' },
      { type: 'figure', caption: 'Figure 18 — Form for Signing In', image: getFigureImage(18) },
      { type: 'text', text: 'Step 3: Access your dashboard—once verified, you\'ll be redirected to the dashboard for your assigned role.' },
      { type: 'figure', caption: 'Figure 19 — Post-login consumer dashboard', image: getFigureImage(19) },
    ],
    figures: [],
  },
  {
    id: 'forgot-password',
    title: '3.3. Forgot Password',
    level: 2,
    content: [],
    contentBlocks: [
      { type: 'text', text: 'Step 1: Click Forgot Password on Login page.' },
      { type: 'figure', caption: 'Figure 20 — Clicking on Forgot Password', image: getFigureImage(20) },
      { type: 'text', text: 'Step 2: Enter your email address and submit.' },
      { type: 'figure', caption: 'Figure 21 — Forgot Password Form', image: getFigureImage(21) },
      { type: 'text', text: 'Step 3: Open the password-reset email and click the provided link.' },
      { type: 'text', text: 'Step 4: Create a new password that meets the on-screen criteria.' },
      { type: 'text', text: 'Step 5: Your password will be updated. Sign in with your new password to access the portal.' },
    ],
    figures: [],
  },
  {
    id: 'joining-organisation',
    title: '3.4. Joining an Organisation',
    level: 2,
    content: [],
    contentBlocks: [
      {
        type: 'text',
        text: 'To publish Datasets, Models, or Use Cases on MahaAgX, users must first associate with a registered organization. By joining an approved entity—such as a government body, startup, research institution, or private company—users are granted an affiliation with the entity post which they can request for an upgrade to provider role. This role provides the necessary permissions to upload and manage digital assets, which are then governed by that organization, ensuring accountability for all published content. The following steps outline how to join an organization that is already registered on the platform.',
      },
      { type: 'text', text: 'Step 1: Click on the "Join an Organisation" button on your dashboard.' },
      { type: 'figure', caption: 'Figure 28 — Accessing Join Organisation Option from the dashboard', image: getFigureImage(28) },
      { type: 'text', text: 'Step 2: You\'ll be redirected to the Join Your Organisation form.' },
      { type: 'figure', caption: 'Figure 29 — Join Organisation Form', image: getFigureImage(29) },
      { type: 'text', text: 'Step 3: From the dropdown, select your organisation from the list of registered entities.' },
      { type: 'figure', caption: 'Figure 30 — Selecting Your Organisation', image: getFigureImage(30) },
      { type: 'text', text: 'Step 4: Fill in your role details:' },
      { type: 'text', text: '1. Job Title: Enter your current designation' },
      { type: 'text', text: '2. Employee ID: Enter your official employee ID associated with the organisation' },
      { type: 'text', text: 'Step 5: Click Submit Details once all required fields are completed.' },
      { type: 'figure', caption: 'Figure 31 — Entering Job Title and Employee ID', image: getFigureImage(31) },
      {
        type: 'text',
        text: 'Step 6: After submission, your request will be sent to the Organisation Manager for review. Once approved, your user profile will be affiliated with your organization.',
      },
    ],
    figures: [],
  },
  {
    id: 'adding-organisation',
    title: '3.5. Adding an Organisation',
    level: 2,
    content: [],
    contentBlocks: [
      {
        type: 'text',
        text: 'If your organization is not yet on MahaAgX, an authorized representative must submit a request to register it. This involves providing the organization\'s details and supporting documents.',
      },
      {
        type: 'text',
        text: 'The person who completes this registration will automatically become the sole Organization Administrator. This role carries significant responsibilities, including:',
      },
      { type: 'text', text: '● Adding and managing organization members.' },
      { type: 'text', text: '● Assigning provider roles.' },
      { type: 'text', text: '● Overseeing all assets published under the organization\'s name.' },
      {
        type: 'text',
        text: 'Once the organization is approved and the individual is designated as its manager/administrator, no other Organization Manager/Administrator accounts can be created for that organization.',
      },
      { type: 'text', text: 'Step 1: Click on the "Join an Organisation" button on your dashboard.' },
      { type: 'figure', caption: 'Figure 32 — Accessing Join Organisation Option from the dashboard', image: getFigureImage(32) },
      {
        type: 'text',
        text: 'Step 2: Search for your organisation. If no match is found, the system will prompt you with an Add Organisation option. Click it to begin the registration process.',
      },
      { type: 'figure', caption: 'Figure 33 — Option to Add Organisation when not found in search', image: getFigureImage(33) },
      {
        type: 'text',
        text: 'Step 3: You will be redirected to the Organisation Registration form. It includes three sections:',
      },
      {
        type: 'text',
        text: '1. Organisation Manager Details: Fill your profile (Name, Organisation email, etc.)',
      },
      {
        type: 'text',
        text: '2. Enter Organisation Details: Fill in entity type, sector, name, website, and address.',
      },
      {
        type: 'text',
        text: '3. Upload Organisation Documents: Upload required files such as, Firm Incorporation Certificate, PAN Card of the organization, Authorization document (stating that user is authorized by their organisation to become Organisation Manager on MahaAgX and manage users and assets on behalf of the organisation).',
      },
      {
        type: 'figure',
        caption: 'Figure 34 — Organisation Registration Form — Organisation Manager Details',
        image: getFigureImage(34),
      },
      {
        type: 'figure',
        caption: 'Figure 35 — Organisation Registration Form — Organisation Details',
        image: getFigureImage(35),
      },
      {
        type: 'figure',
        caption: 'Figure 36 — Organisation Registration Form — Upload Organisation Documents',
        image: getFigureImage(36),
      },
      { type: 'text', text: 'Step 4: Once the form is completed and documents uploaded, click Submit Details.' },
      {
        type: 'text',
        text: 'Step 5: A confirmation pop-up will appear indicating that the request has been successfully sent to the MahaAgX Platform Administrator for review. After the request is reviewed and approved, the user will be assigned the Organisation Manager role and can begin managing users and assets under the new organisation.',
      },
    ],
    figures: [],
  },
  ...USER_MANUAL_SECTION_4,
  ...USER_MANUAL_SECTION_5,
  {
    id: 'user-profile',
    title: '6. User Profile Management',
    level: 1,
    content: [],
    contentBlocks: [
      {
        type: 'text',
        text: 'The My Profile section allows users to manage their personal information, account settings, and platform consents. This screen helps users stay in control of their MahaAgX identity and preferences. Key sections include:',
      },
      {
        type: 'text',
        text: '1. User Details: Displays basic profile information including,',
        indent: 1,
      },
      { type: 'text', text: 'a. Full Name', indent: 2 },
      { type: 'text', text: 'b. Username', indent: 2 },
      { type: 'text', text: 'c. Email ID', indent: 2 },
      { type: 'text', text: 'd. Phone Number', indent: 2 },
      {
        type: 'text',
        text: 'These fields can be edited by clicking Edit Profile (top right) and saved with Save Changes.',
      },
      { type: 'text', text: '2. Account Settings: Options to manage,', indent: 1 },
      {
        type: 'text',
        text: 'a. API Key creation for integrating MahaAgX programmatically',
        indent: 2,
      },
      { type: 'text', text: 'b. Change Password for account security', indent: 2 },
      {
        type: 'text',
        text: 'c. Delete Account to permanently remove the user from MahaAgX. Please note, in case of provider/provider with compute/organization manager, users have an option to transfer ownership of their uploaded assets before deleting their account.',
        indent: 2,
      },
      {
        type: 'text',
        text: '3. Consent Management: Tracks user agreements for:',
        indent: 1,
      },
      {
        type: 'text',
        text: 'a. Privacy Policy Consent: Users can view or revoke consent for personal data processing.',
        indent: 2,
      },
      {
        type: 'text',
        text: 'b. Terms of Service Consent: Users can view or revoke their agreement to the platform\'s terms.',
        indent: 2,
      },
      {
        type: 'text',
        text: 'Users should regularly visit this section to keep their profile updated, manage their access, and ensure continued compliance with MahaAgX policies.',
      },
      {
        type: 'figure',
        caption: 'Figure 92 — My Profile Section',
        image: getFigureImage(92),
      },
    ],
    figures: [],
  },
  {
    id: 'help-support',
    title: '7. Accessing Help and Support Services',
    level: 1,
    content: ['Guides users to find self-help resources and raise support tickets.'],
    figures: [],
  },
  {
    id: 'help-faqs',
    title: '7.1. FAQs',
    level: 2,
    content: [],
    contentBlocks: [
      {
        type: 'text',
        text: 'The FAQs section provides quick answers to common questions about MahaAgX features. It is organized into the following categories for easy navigation:',
      },
      {
        type: 'text',
        text: '1. General Queries: Overview questions about MahaAgX and its functionality',
        indent: 1,
      },
      {
        type: 'text',
        text: '2. User Management: Guidance on roles, permissions, and user actions',
        indent: 1,
      },
      {
        type: 'text',
        text: '3. Login/Registration: Help with account setup, login issues',
        indent: 1,
      },
      {
        type: 'text',
        text: '4. Datasets: Using, accessing, and contributing to the Dataset',
        indent: 1,
      },
      {
        type: 'text',
        text: '5. Models: Questions related to uploading, downloading, and using Models',
        indent: 1,
      },
      {
        type: 'text',
        text: '6. Use cases: Understanding how use cases are structured and published',
        indent: 1,
      },
      {
        type: 'text',
        text: '7. Sandbox IDE: Assistance with creating notebooks, accessing compute, and using toolsets',
        indent: 1,
      },
      {
        type: 'text',
        text: '8. Organisations: Joining or managing organisations and assigning roles',
        indent: 1,
      },
      {
        type: 'text',
        text: '9. Contact Us: Support details for reaching the MahaAgX helpdesk',
        indent: 1,
      },
      {
        type: 'figure',
        caption: 'Figure 93 — FAQs Section',
        image: getFigureImage(93),
      },
    ],
    figures: [],
  },
];
